import {
  colors,
  cssSpacingXs,
  cssSpacingSm,
  cssSpacingMd,
  cssSpacingLg,
  cssSpacingXl,
  cssSpacingXxl,
  cssSpacingXxxl,
  cssFontSizeLarge,
  cssLogoHeight,
  cssMaxContentWidth,
  cssMaxStackWidth,
  cssMaxCardWidth,
  cssMinCardHeight,
  cssMinButtonWidth,
  cssLineHeightRelaxed,
  cssLetterSpacingWide,
  typography,
  layout,
} from '../constants/globalcss';

// Use existing global constants - no more local definitions
const sizes = {
  borderRadius: {
    large: '20px',
    medium: layout.borderRadius.medium,
    small: layout.borderRadius.small,
  },
  dimensions: {
    logoHeight: cssLogoHeight,
    maxCardWidth: cssMaxCardWidth,
    maxContentWidth: cssMaxContentWidth,
    maxStackWidth: cssMaxStackWidth,
    minButtonWidth: cssMinButtonWidth,
    minCardHeight: cssMinCardHeight,
  },
  fontSize: {
    body: cssFontSizeLarge, // 16px
  },
  fontWeight: {
    bold: typography.fontWeight.bold,
    normal: typography.fontWeight.regular,
    semiBold: typography.fontWeight.semibold,
  },
  letterSpacing: {
    wide: cssLetterSpacingWide,
  },
  lineHeight: {
    relaxed: cssLineHeightRelaxed,
  },
  spacing: {
    lg: cssSpacingLg, // 16px
    md: cssSpacingMd, // 12px
    sm: cssSpacingSm, // 8px
    xl: cssSpacingXl, // 32px
    xs: cssSpacingXs, // 4px
    xxl: cssSpacingXxl, // 40px
    xxxl: cssSpacingXxxl, // 60px
  },
};

// Base styles that are reused
const baseButton = {
  alignItems: 'center',
  border: 'none',
  borderRadius: sizes.borderRadius.medium,
  cursor: 'pointer',
  display: 'flex',
  fontSize: sizes.fontSize.body,
  fontWeight: sizes.fontWeight.semiBold,
  gap: sizes.spacing.sm,
  height: '48px',
  justifyContent: 'center',
  letterSpacing: sizes.letterSpacing.wide,
  minWidth: sizes.dimensions.minButtonWidth,
  padding: `${sizes.spacing.md} ${sizes.spacing.lg}`,
  textTransform: 'uppercase' as const,
  transition: 'background-color 0.3s ease, color 0.3s ease', // Only color transitions
  width: '100%',
};

const baseFlex = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
};

const baseShadow = 'rgba(0,0,0,0.1)';

export const commonStyles = {
  // Container styles
  contentStack: {
    maxWidth: sizes.dimensions.maxStackWidth,
    width: '100%',
  },

  // Page layout styles
  contentWrapper: {
    display: 'flex',
    flex: 1,
  },

  // Typography styles
  descriptionText: {
    color: colors.text.primary,
    fontWeight: sizes.fontWeight.normal,
    lineHeight: sizes.lineHeight.relaxed,
    maxWidth: sizes.dimensions.maxContentWidth,
  },

  divider: {
    borderColor: 'rgba(0,0,0,0.12)',
    borderWidth: '1px',
  },

  header: {
    ...baseFlex,
    background: colors.primary,
    color: colors.background.paper,
    gap: 2,
    padding: `${sizes.spacing.xxl} ${sizes.spacing.xxl} 20px`,
  },

  headerTitle: {
    color: colors.background.paper,
    fontWeight: sizes.fontWeight.bold,
  },

  logo: {
    height: sizes.dimensions.logoHeight,
    width: 'auto',
  },

  mainCard: {
    background: colors.background.paper,
    borderRadius: sizes.borderRadius.large,
    boxShadow: `0 20px 60px ${baseShadow}`,
    display: 'flex',
    flexDirection: 'column' as const,
    maxWidth: sizes.dimensions.maxCardWidth,
    minHeight: sizes.dimensions.minCardHeight,
    overflow: 'hidden',
    width: '100%',
  },

  pageContainer: {
    ...baseFlex,
    fontFamily: typography.fontFamily,
    height: '100vh',
    padding: sizes.spacing.xxl,
  },

  // Button styles - using base button with specific overrides
  primaryButton: {
    ...baseButton,
    '&:hover': {
      backgroundColor: colors.secondary,
    },
    background: colors.primary,
    boxShadow: `0 4px 15px ${baseShadow}`,
    color: colors.background.paper,
  },

  secondaryButton: {
    ...baseButton,
    '&:hover': {
      backgroundColor: colors.primary,
      color: colors.background.paper,
    },
    background: 'transparent',
    border: `2px solid ${colors.primary}`,
    boxShadow: 'none',
    color: colors.primary,
  },

  sectionContainer: {
    ...baseFlex,
    flex: 1,
    flexDirection: 'column' as const,
    padding: `${sizes.spacing.xxxl} ${sizes.spacing.xxl}`,
  },

  sectionTitle: {
    color: colors.primary,
    fontWeight: sizes.fontWeight.bold,
    marginBottom: sizes.spacing.sm,
    textAlign: 'center' as const,
  },
};

// Simplified button hover handlers - only color changes, no animations
export const buttonHoverHandlers = {
  primaryButtonHover: (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.target as HTMLButtonElement;
    target.style.background = colors.secondary;
  },

  primaryButtonLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.target as HTMLButtonElement;
    target.style.background = colors.primary;
  },

  secondaryButtonHover: (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.target as HTMLButtonElement;
    target.style.background = colors.primary;
    target.style.color = colors.background.paper;
  },

  secondaryButtonLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.target as HTMLButtonElement;
    target.style.background = 'transparent';
    target.style.color = colors.primary;
  },
};

export { sizes };
